import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import { cn } from '@/lib/utils';

marked.setOptions({
  // Match NoteEditor (breaks:false): single \n is NOT a <br>; \n\n is
  // a paragraph break. Lets H1/H2 apply to individual paragraphs as
  // users intuitively expect from any editor.
  breaks: false,
  gfm: true,
});

/**
 * DOMPurify's built-in CSS parser silently strips style properties whose
 * values it doesn't recognize (oklch() is one such case). That ate the
 * color marks TipTap was emitting when wrapped around bold/italic.
 *
 * Workaround: a sanitize-attribute hook that takes over `style` handling
 * — we filter the property list ourselves, allowlisting only `color` and
 * `background-color`, and keep the raw value. Both props are safe (no
 * URL parsing, no script injection vector).
 *
 * Hook is added once at module load; DOMPurify calls it on every sanitize. */
DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  if (data.attrName !== 'style') return;
  const safeProps = data.attrValue
    .split(';')
    .map((p) => p.trim())
    .filter((p) => /^(color|background-color)\s*:/i.test(p))
    .join('; ');
  if (safeProps) {
    data.attrValue = safeProps;
  } else {
    data.keepAttr = false;
  }
});

/** Sanitize config used for both HTML-source and markdown-source paths.
 *  Allows the inline marks TipTap emits (textStyle color + highlight bg)
 *  via a whitelisted `style` attribute. */
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 's', 'del', 'mark', 'span',
    'h2', 'h3', 'h4',
    'ul', 'ol', 'li',
    'blockquote', 'code', 'pre',
    'a', 'hr',
  ],
  ALLOWED_ATTR: ['href', 'rel', 'target', 'style', 'data-color'],
};

/** Cheap heuristic — TipTap's getHTML() always wraps content in block
 *  tags so the first non-whitespace char is `<`. Legacy markdown notes
 *  start with text or `#`/`*`/`-` etc. */
function looksLikeHtml(source: string): boolean {
  return /^\s*<[a-zA-Z]/.test(source);
}

/** Render note source → sanitized HTML for read-only display.
 *
 *  - HTML source (new TipTap notes): inject directly after sanitize so
 *    colors + highlights round-trip intact.
 *  - Markdown source (legacy notes): parse via marked, then sanitize.
 *
 *  Same DOMPurify allowlist both paths. */
export function MarkdownView({
  source,
  className,
}: {
  source: string;
  className?: string;
}) {
  const html = useMemo(() => {
    const raw = looksLikeHtml(source)
      ? source
      : (marked.parse(source, { async: false }) as string);
    return DOMPurify.sanitize(raw, PURIFY_CONFIG);
  }, [source]);

  return (
    <div
      className={cn(
        'prose prose-invert prose-sm max-w-none',
        'prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1',
        'prose-headings:font-semibold prose-a:text-primary',
        'prose-blockquote:border-l-primary/60',
        // Inline marks must inherit color so a wrapping <span style="color:…">
        // cascades through bold/italic/code. Without these overrides,
        // prose-invert's own color rules win and you get the "bold can't
        // be colored" bug.
        'prose-strong:text-inherit prose-em:text-inherit prose-code:text-inherit prose-blockquote:text-inherit prose-headings:text-inherit',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
